// Paneles alimentados por la ficha pública del portal, que se lee bajo demanda
// al analizar la licitación con IA. Son datos que ninguna API de Mercado
// Público entrega: cómo se evalúa la oferta, qué garantías se exigen y qué
// antecedentes hay que reunir para poder postular.
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FileCheck2, FileWarning, Scale, ShieldCheck } from "lucide-react";
import type { Licitacion } from "@/types/licitacion";

const fmtFecha = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("es-CL", {
        day: "2-digit",
        month: "short",
        timeZone: "America/Santiago",
      })
    : null;

/**
 * De dónde salen estos datos y cuándo. Existe porque un fallo de lectura se
 * veía igual que un "todavía no se intentó": el usuario decide sobre la
 * licitación y tiene que saber si está mirando la ficha real o sólo lo que
 * entrega la API.
 */
export function OrigenFichaBadge({ licitacion }: { licitacion: Licitacion }) {
  const { fichaEstado, fichaSyncedAt, fichaIntentoAt, fichaError } = licitacion;
  if (!fichaEstado) {
    return (
      <Badge variant="outline" className="gap-1 font-normal">
        Ficha del portal sin leer
      </Badge>
    );
  }
  const fallo = fichaEstado === "error";
  const texto =
    fichaEstado === "ok"
      ? `Ficha del portal · ${fmtFecha(fichaSyncedAt) ?? "leída"}`
      : fichaEstado === "sin_datos"
        ? "El portal no publica bases"
        : "No se pudo leer la ficha";
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant={fallo ? "destructive" : "secondary"} className="gap-1 font-normal">
            {fallo && <FileWarning className="h-3 w-3" aria-hidden />}
            {texto}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          {fichaEstado === "ok"
            ? "Criterios, garantías y antecedentes provienen de la ficha pública de Mercado Público."
            : fichaEstado === "sin_datos"
              ? "La ficha se leyó correctamente, pero este proceso no publica bases con criterios ni garantías."
              : `Último intento ${fmtFecha(fichaIntentoAt) ?? "reciente"}${
                  fichaError ? `: ${fichaError}` : ""
                }. Se reintenta en el próximo análisis con IA.`}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/** La ficha se lee al analizar con IA; antes de eso no hay nada que mostrar. */
function SinFicha({ que, licitacion }: { que: string; licitacion: Licitacion }) {
  if (licitacion.fichaEstado === "error") {
    return (
      <p className="text-sm text-muted-foreground">
        No se pudo leer la ficha del portal, así que {que.toLowerCase()} no están disponibles.
        Se reintenta en el próximo análisis con IA.
      </p>
    );
  }
  return (
    <p className="text-sm text-muted-foreground">
      {que} se leen de la ficha del portal al analizar la licitación con IA. Ejecuta el
      análisis para completarlos.
    </p>
  );
}

function Vacio({ texto }: { texto: string }) {
  return <p className="text-sm text-muted-foreground">{texto}</p>;
}

/**
 * Criterios de evaluación con su ponderación: es lo que decide la
 * adjudicación, así que va con el peso bien visible.
 */
export function CriteriosEvaluacionPanel({ licitacion }: { licitacion: Licitacion }) {
  const criterios = licitacion.criteriosEvaluacion ?? [];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Scale className="h-4 w-4 text-muted-foreground" aria-hidden />
          Criterios de evaluación
          {criterios.length > 0 && <Badge variant="secondary">{criterios.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!licitacion.fichaSyncedAt ? (
          <SinFicha que="Los criterios de evaluación" licitacion={licitacion} />
        ) : criterios.length === 0 ? (
          <Vacio texto="El organismo no publicó criterios de evaluación en la ficha." />
        ) : (
          criterios.map((c, i) => (
            <div
              key={`${c.numero ?? i}-${c.nombre}`}
              className="rounded-lg border border-border p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium">{c.nombre}</p>
                {c.ponderacion && (
                  <Badge variant="outline" className="shrink-0 tabular-nums">
                    {c.ponderacion}
                  </Badge>
                )}
              </div>
              {c.observaciones && (
                <p className="mt-2 text-sm text-muted-foreground">{c.observaciones}</p>
              )}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Garantías exigidas. El monto se muestra tal como lo publica el organismo:
 * puede venir en pesos o como porcentaje del contrato, y `moneda` es lo que
 * los distingue — convertirlo sería inventar una cifra.
 */
export function GarantiasPanel({ licitacion }: { licitacion: Licitacion }) {
  const garantias = licitacion.garantias ?? [];
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" aria-hidden />
          Garantías exigidas
          {garantias.length > 0 && <Badge variant="secondary">{garantias.length}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!licitacion.fichaSyncedAt ? (
          <SinFicha que="Las garantías exigidas" licitacion={licitacion} />
        ) : garantias.length === 0 ? (
          <Vacio texto="Esta licitación no exige garantías." />
        ) : (
          garantias.map((g, i) => (
            <div key={i} className="rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-medium tabular-nums">
                  {g.monto ?? "—"}
                  {g.moneda ? ` ${g.moneda}` : ""}
                </p>
                {g.vencimiento && (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    Vence {g.vencimiento}
                  </span>
                )}
              </div>
              {g.beneficiario && (
                <p className="mt-1 text-xs text-muted-foreground">
                  A favor de {g.beneficiario}
                </p>
              )}
              {g.glosa && <p className="mt-2 text-sm text-muted-foreground">{g.glosa}</p>}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function GrupoRequisitos({ titulo, items }: { titulo: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {titulo}
      </p>
      <ul className="mt-2 space-y-2">
        {items.map((r, i) => (
          <li key={i} className="rounded-lg border border-border p-3 text-sm">
            {r}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Lo que hay que reunir para postular: antecedentes administrativos, técnicos
 * y económicos, más los documentos legales que piden las bases.
 */
export function RequisitosOfertaPanel({ licitacion }: { licitacion: Licitacion }) {
  const req = licitacion.requisitosOferta;
  const documentos = licitacion.documentosExigidos ?? [];
  const total =
    (req?.administrativo.length ?? 0) +
    (req?.tecnico.length ?? 0) +
    (req?.economico.length ?? 0) +
    documentos.length;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileCheck2 className="h-4 w-4 text-muted-foreground" aria-hidden />
          Antecedentes para postular
          {total > 0 && <Badge variant="secondary">{total}</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!licitacion.fichaSyncedAt ? (
          <SinFicha que="Los antecedentes exigidos" licitacion={licitacion} />
        ) : total === 0 ? (
          <Vacio texto="La ficha no detalla antecedentes; revisa las bases adjuntas." />
        ) : (
          <>
            <GrupoRequisitos titulo="Administrativos" items={req?.administrativo ?? []} />
            <GrupoRequisitos titulo="Técnicos" items={req?.tecnico ?? []} />
            <GrupoRequisitos titulo="Económicos" items={req?.economico ?? []} />
            <GrupoRequisitos titulo="Documentos legales" items={documentos} />
          </>
        )}
      </CardContent>
    </Card>
  );
}
